import { CompanyHeader } from '@company/components/display/headers';
import { CompanyManagement } from '@company/components/layout/CompanyManagement/CompanyManagement';
import { CompanyManagementContext } from '@company/contexts/CompanyManagementContext';
import { useGetCompanyUserRoleQuery, useGetUserCompaniesQuery } from '@company/services/company';
import { ICompany } from '@company/types/company';
import { LoadingContainer } from '@components/display/loading/LoadingContainer';
import { PublicLayoutContent } from '@components/layout/public';
import { PUBLIC_PADDING_CLASSNAME } from '@constants/navigation';
import { useAppDispatch } from '@hooks/generic';
import { setTitle } from '@store/appSlice';
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NotFound } from 'src/routes/NotFound';

const CompanyOverview = () => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const { data: userCompaniesResponse, isLoading: isUserCompaniesLoading } = useGetUserCompaniesQuery({});
    const userCompanies = userCompaniesResponse as ICompany[] | undefined;

    const selectedCompany = useMemo(
        () =>
            searchParams.get('id') && !isUserCompaniesLoading
                ? userCompanies?.find((company) => company.id === searchParams.get('id'))
                : userCompanies?.[0],
        [isUserCompaniesLoading, searchParams, userCompanies]
    );
    const { data: user, isLoading: isCompanyUserRoleLoading } = useGetCompanyUserRoleQuery(
        { company_id: selectedCompany?.id ?? '' },
        { skip: !selectedCompany }
    );

    const isOwner = useMemo(() => !!user?.is_owner, [user]);
    const permissions = useMemo(
        () => ({
            filter: isOwner,
            remove: isOwner,
            edit: isOwner,
            partnerships: {
                view: isOwner,
                manage: isOwner,
            },
        }),
        [isOwner]
    );

    const isLoading = useMemo(
        () => isUserCompaniesLoading || isCompanyUserRoleLoading,
        [isCompanyUserRoleLoading, isUserCompaniesLoading]
    );

    useEffect(() => {
        dispatch(setTitle('Company Overview'));
    }, [dispatch]);

    return (
        <>
            {selectedCompany && searchParams.get('id') ? <title>Company: {selectedCompany?.name}</title> : null}
            <PublicLayoutContent dottedBackgroundProps={{ gradient: true, gradientValue: '10%' }}>
                <CompanyManagementContext.Provider value={{ permissions, company: selectedCompany }}>
                    <LoadingContainer isLoading={isLoading}>
                        <div className={`${PUBLIC_PADDING_CLASSNAME} font-inter h-full`}>
                            <CompanyHeader className="mb-10" />
                            {userCompanies && userCompanies.length > 0 && selectedCompany ? (
                                <CompanyManagement />
                            ) : Number(userCompanies?.length) <= 0 || !userCompanies ? (
                                <div>No companies available</div>
                            ) : (
                                <NotFound />
                            )}
                        </div>
                    </LoadingContainer>
                </CompanyManagementContext.Provider>
            </PublicLayoutContent>
        </>
    );
};

export { CompanyOverview };
